import { forwardRef, RefObject, HTMLAttributes, ReactNode, isValidElement } from 'react';
import { Link, ILinkProps } from '@base/lib/Link';
import cn from 'classnames';
import { IconLoading } from '@base/icon';
import { theme } from 'pro/config/theme';

export type TButtonType = 'base' | 'no-style' | 'default' | 'primary' | 'dashed' | 'text' | 'link' | string;

export type TButtonSize = 'middle' | 'small' | 'large' | 'mini' | false;

export type TButtonShape = 'default' | 'circle' | 'round';

type IBaseProps = {
    type?: TButtonType;
    size?: TButtonSize;
    shape?: TButtonShape;
    block?: boolean;
    loading?: boolean;
    disabled?: boolean;
    icon?: ReactNode;
    text?: ReactNode;
    children?: ReactNode;
    className?: string;
}

type ILinkTagProps = IBaseProps & ILinkProps;

type IButtonTagProps = IBaseProps & Omit<HTMLAttributes<HTMLButtonElement>, 'type'> & {
    htmlType?: 'button' | 'submit';
};

export type ButtonProps = ILinkTagProps | IButtonTagProps;

const sizeMap = {
    middle: 'md',
    small: 'sm',
    large: 'lg',
    mini: 'xs',
}

function getClass(props: ButtonProps): string {
    const {
        className,
        type: propsType = 'default',
        size = 'middle',
        shape = 'default',
        loading = false,
        disabled = false,
        block = false,
        icon,
        children
    } = props;

    const type = theme.buttonTypes.includes(propsType) ? propsType : 'default';

    return cn(
        className,
        type === 'no-style' ? 'sw-btn-no-style' : cn(
            'sw-btn',
            `sw-btn-${type}`,
            size === false ? '' : `sw-btn-${sizeMap[size]}`
        ),
        { 'sw-btn-disabled': disabled },
        { 'sw-btn-loading': loading },
        { 'sw-btn-full': block },
        { 'sw-btn-icon-only': !!icon && !children },
        { [`sw-btn-${shape}`]: shape !== 'default' },
    );
}

export const ButtonIcon: Globals.FC = ({ children }) => {
    return (
        <span className={'sw-btn-icon'}>
            {children}
        </span>
    )
}

export const Button = forwardRef<HTMLAnchorElement | HTMLButtonElement, ButtonProps>((props, ref) => {

    const {
        // Base
        type = 'default',
        size = 'middle',
        shape = 'default',
        block = false,
        loading = false,
        disabled = false,
        icon,
        text: _text,
        className,
        children,
        ...rest
    } = props;

    const text = _text || children;

    if ('href' in rest) {
        return (
            <Link
                {...rest}
                ref={ref as RefObject<HTMLAnchorElement>}
                className={getClass(props)}
            >
                {loading && <ButtonIcon><IconLoading size={'100%'}/></ButtonIcon>}
                {icon && !loading && <ButtonIcon>{icon}</ButtonIcon>}
                {isValidElement(text) || type === 'no-style'
                    ? text
                    : (text ? <span className={'sw-text'}>{text}</span> : null)
                }
            </Link>
        )
    }

    const { htmlType = 'button', ...buttonRest } = rest;

    return (
        <button
            {...buttonRest}
            ref={ref as RefObject<HTMLButtonElement>}
            type={htmlType}
            className={getClass(props)}
            disabled={disabled || loading}
        >
            {loading && <ButtonIcon><IconLoading size={'100%'}/></ButtonIcon>}
            {icon && !loading && <ButtonIcon>{icon}</ButtonIcon>}
            {isValidElement(text) || type === 'no-style'
                ? text
                : (text ? <span className={'sw-text'}>{text}</span> : null)
            }
        </button>
    )
});